import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { API_BASE_URL, getOrgId, getOrCreateDeviceId } from "../config/api";
import GearOrderEditDialog from "../components/GearOrderEditDialog";
import PasswordTextField from "../components/PasswordTextField";

// Deliberately separate from apiRequest: this page's token is a narrow
// `role: 'registration'` JWT (only valid for the two registration-scoped
// gear routes), not a player/admin session. apiRequest's 401/403 handling
// clears playerAuthToken/authToken and redirects to /login or /player/login
// based on which of THOSE tokens was used - it has no concept of this third
// token type, so reusing it here risks logging out an unrelated admin/player
// session in the same browser or redirecting this page somewhere wrong.
const TOKEN_KEY = "registrationAuthToken";

function registrationApiRequest(
  endpoint: string,
  options: RequestInit = {},
): Promise<Response> {
  const token = localStorage.getItem(TOKEN_KEY);
  return fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "X-Organization-Id": getOrgId(),
      "X-Device-Id": getOrCreateDeviceId(),
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
}

export default function GearOrderLoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState("");
  const [loggedIn, setLoggedIn] = useState(
    () => !!localStorage.getItem(TOKEN_KEY),
  );

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoggingIn(true);
    try {
      const response = await registrationApiRequest(
        "/api/season-registration/registration-login",
        {
          method: "POST",
          body: JSON.stringify({ username, password }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setLoggedIn(true);
    } catch (err: any) {
      setError(err.message || "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setLoggedIn(false);
    setUsername("");
    setPassword("");
  };

  if (loggedIn) {
    return (
      <GearOrderEditDialog
        open
        onClose={handleLogout}
        fetchFn={registrationApiRequest}
        getEndpoint="/api/season-registration/my-pending-registration"
        updateEndpoint="/api/season-registration/gear-order-pending"
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        bgcolor: "grey.50",
      }}
    >
      <Container maxWidth="xs">
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Modify Gear Order
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Log in with the username and password you set when you
            registered to change your gear order.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin}>
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              autoFocus
            />
            <PasswordTextField
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loggingIn || !username || !password}
            >
              {loggingIn ? <CircularProgress size={20} /> : "Log In"}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
